import React, { useEffect, useRef } from 'react';
import { connect } from 'react-redux';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import { get } from 'lodash-es';

import Container from 'reactstrap/lib/Container';

import { insiderMethods } from 'site-modules/shared/components/profile/insider-methods';
import { connectToModel } from 'client/data/luckdragon/redux/react-binding';
import { UserProfileModel } from 'client/data/models/profile/profile';
import { TrackingHandler } from 'client/tracking/handler';
import { InsiderNavigation } from 'site-modules/profile/components/shared/insider-navigation/insider-navigation';
import { ErrorBoundary } from 'site-modules/shared/components/error-boundary/error-boundary';
import { EventToolbox } from 'client/utils/event-toolbox';
import { PAGE_CONTENT_UPDATE } from 'client/tracking/constant';
import { IDM_METHODS } from 'client/data/models/profile/profile-constants';
import { AccessDashboard } from 'site-modules/profile/components/shared/access-dashboard/access-dashboard';

import { StickyBanner } from 'site-modules/shared/components/sticky-banner/sticky-banner';
import { FeatureFlag } from 'site-modules/shared/components/feature-flag/feature-flag';
import { MrectNative } from 'site-modules/shared/components/native-ad/mrect/mrect-native';
import { Experiment, Recipe } from 'site-modules/shared/components/experiment';

import './insider-page.scss';

export const DEFAULT_OPTIONS = {
  loadIdmData: true,
  inheritMethodsOnly: false,
};

export function insiderPage(WrappedComponent, tracking = {}, options = DEFAULT_OPTIONS) {
  const { loadIdmData, inheritMethodsOnly } = options;

  function InsiderPageWrapper({
    isAuthenticated = null,
    isAnonymousUser,
    isDeletedUser = false,
    userHasEmail = false,
    hasDataIdm,
    isMobile = false,
    setModelValue,
    slug = '',
    updateIdmData,
    removeDataFromIdm,
    ...restProps
  }) {
    const isInitialAuthenticated = useRef(isAuthenticated);
    const isInitialHasDataIdm = useRef(hasDataIdm);

    useEffect(() => {
      if (isAuthenticated && !isInitialAuthenticated.current) {
        EventToolbox.fireCustomEvent(PAGE_CONTENT_UPDATE);
      }
    }, [isAuthenticated]);

    useEffect(() => {
      if (hasDataIdm && !isInitialHasDataIdm.current && loadIdmData) {
        setModelValue('data.idm', UserProfileModel, { method: IDM_METHODS.LOAD });
      }
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [hasDataIdm]);

    useEffect(() => {
      const { map: trackingMap = {} } = tracking;
      TrackingHandler.useMap(trackingMap);
    }, []);

    const { parent: trackingParent } = tracking;
    const wrappedComponentProps = {
      isAuthenticated,
      isAnonymousUser,
      userHasEmail,
      hasDataIdm,
      setModelValue,
      slug,
      updateIdmData,
      removeDataFromIdm,
      ...restProps,
      isDeletedUser,
      isMobile,
    };

    const navigationProps = { slug, isMobile };

    const containerClassNames = {
      loaded: isAuthenticated && hasDataIdm,
      'not-loaded': isAuthenticated && !hasDataIdm,
    };

    if (inheritMethodsOnly) {
      return <WrappedComponent {...wrappedComponentProps} />;
    }

    return isAuthenticated || isDeletedUser ? (
      <ErrorBoundary>
        {!isMobile && !isDeletedUser && <InsiderNavigation {...navigationProps} />}
        <Container
          className={classNames('insider-page-container standard-spacing', containerClassNames)}
          data-tracking-parent={trackingParent}
        >
          <main id="main-content" tabIndex={-1}>
            <WrappedComponent {...wrappedComponentProps} />
            {isMobile && !slug && <InsiderNavigation {...navigationProps} />}
          </main>
          <Experiment name="ads-9299" showDefault>
            <Recipe name="ctrl" isDefault />
            <Recipe name="chal">
              <MrectNative wrapperClass="mrect" refreshable={false} useMinHeight />
              <FeatureFlag name="ads">
                <StickyBanner isHomepage={false} />
              </FeatureFlag>
            </Recipe>
          </Experiment>
        </Container>
      </ErrorBoundary>
    ) : (
      <ErrorBoundary>
        <AccessDashboard isAuthenticated={isAuthenticated} />
      </ErrorBoundary>
    );
  }

  InsiderPageWrapper.propTypes = {
    hasDataIdm: PropTypes.bool.isRequired,
    isAuthenticated: PropTypes.bool,
    isDeletedUser: PropTypes.bool,
    userHasEmail: PropTypes.bool,
    setModelValue: PropTypes.func.isRequired,
    isMobile: PropTypes.bool,
    slug: PropTypes.string,
    updateIdmData: PropTypes.func.isRequired,
    removeDataFromIdm: PropTypes.func.isRequired,
  };

  const InsiderPageIdmWrapper = insiderMethods(InsiderPageWrapper);

  const mapStateToProps = state => ({
    hasDataIdm: !!get(state, 'profile.data.idm'),
    isAuthenticated: get(state, 'profile.isAuthenticated'),
    isAnonymousUser: get(state, 'profile.isAnonymousUser'),
    isDeletedUser: get(state, 'profile.isDeletedUser'),
    userHasEmail: !!get(state, 'profile.data.idm.identifiers.email[0]'),
    isMobile: state.mobile,
    hasProfileScreen: !!get(state, 'profile.screen'),
  });

  return connect(mapStateToProps)(connectToModel(InsiderPageIdmWrapper, {}));
}
